import { useEffect, useSyncExternalStore } from 'react';

/**
 * The current page's own crumb for the top bar's breadcrumb (12 Oct 2026, the practitioner app's grammar): a page that
 * is ONE thing (a patient, an appointment) names it, so the bar reads "Patients › Omar Al Suwaidi" instead of
 * "Patients › Edit patient". The page sets it while mounted; it clears on leave.
 */
let label: string | null = null;
const subs = new Set<() => void>();
const set = (l: string | null) => { if (label === l) return; label = l; subs.forEach((f) => f()); };

export function useSetBarCrumb(l: string | null): void {
  useEffect(() => { set(l); return () => set(null); }, [l]);
}

export function useBarCrumb(): string | null {
  return useSyncExternalStore((f) => { subs.add(f); return () => { subs.delete(f); }; }, () => label, () => label);
}

/**
 * A page that knows its whole place in the app hands the top bar its full trail (13 Oct 2026, Antonin: the Studio
 * editors drew their own breadcrumb under the bar's — "use the main top one"). Ancestors carry their click; the last
 * item is the page itself. Set while mounted, cleared on leave; while set, the bar shows it instead of the trail it
 * would derive from the address.
 */
/** An ancestor carries its click, or an address the bar navigates to (for pages that render outside the router). */
export interface BarTrailItem { label: string; onClick?: () => void; to?: string }
let trail: BarTrailItem[] | null = null;
const trailSubs = new Set<() => void>();

export function useSetBarTrail(items: BarTrailItem[] | null): void {
  // Keyed by the labels: the click handlers are new on every render, the trail only changes when a label does.
  const key = items ? items.map((i) => i.label).join('\u0000') : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { trail = items; trailSubs.forEach((f) => f()); return () => { trail = null; trailSubs.forEach((f) => f()); }; }, [key]);
}

export function useBarTrail(): BarTrailItem[] | null {
  return useSyncExternalStore((f) => { trailSubs.add(f); return () => { trailSubs.delete(f); }; }, () => trail, () => trail);
}

/** The trail as it stands, outside React (tests read it to check a page's place and click its ancestors). */
export function getBarTrail(): BarTrailItem[] | null {
  return trail;
}
